import { useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Building2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Save,
  Search,
  Users,
  X,
} from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import type {
  EpcProjectMembershipRow,
  EpcProjectOrgUnit,
} from '../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../types/scheduleTask';
import { computeDeptCoverage, deptLoadPct, deptTeamSize, type DeptCoverage } from './utils/deptCoverage';
import {
  applyOrgUnitToTaskIndices,
  departmentHeadForOrg,
  orgUnitKey,
  orgUnitName,
  taskOrgUnitId,
} from './utils/epcScheduleDepartmentAssignment';
import { suggestOrgUnitsForTask } from './utils/epcScheduleAutoAssignment';
import { departmentToneForOrgId, getDepartmentChipClasses } from './utils/epcScheduleDepartmentColors';
import { buildWbsTree, type EpcWbsNode } from './utils/epcScheduleWbsTree';
import { countOverwrites, selectionSummary, toggleSelectionWithDescendants } from './utils/wbsSelection';

export type EpcScheduleDepartmentAssignViewProps = {
  tasks: EpcScheduleTaskRow[];
  onChange: (next: EpcScheduleTaskRow[]) => void;
  orgUnits: EpcProjectOrgUnit[];
  memberships: EpcProjectMembershipRow[];
  canUpdate: boolean;
  saving: boolean;
  dirty: boolean;
  onSave: () => void | Promise<void>;
};

/** Display-only focus-area hint for an unassigned row: `matches "<token>" · <Dept>` (spec §4.3 — never auto-applied). */
export function focusHintForTask(
  task: EpcScheduleTaskRow,
  orgUnits: EpcProjectOrgUnit[]
): string | null {
  const top = suggestOrgUnitsForTask(task, orgUnits)[0];
  if (!top || top.matchedTokens.length === 0) return null;
  const dept = orgUnitName(orgUnits, top.orgId);
  if (!dept) return null;
  return `matches "${top.matchedTokens[0]}" · ${dept}`;
}

function pillClasses(on: boolean): string {
  return twMerge(
    'rounded-full border px-2.5 py-1 text-[10px] font-semibold transition',
    on
      ? 'border-[#a7f3d0] bg-[#ecfdf5] text-[#047857]'
      : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
  );
}

/* ============================================================
 *  Coverage strip (spec §4.3 top card)
 * ============================================================ */

function CoverageCell({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="border-l border-slate-100 px-4 py-3 first:border-l-0">
      <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400">{label}</div>
      <div
        aria-label={label}
        className={twMerge('mt-0.5 text-lg font-bold tabular-nums text-slate-900', valueClass)}
      >
        {value}
      </div>
    </div>
  );
}

function CoverageStrip({ coverage }: { coverage: DeptCoverage }) {
  const unassignedPct =
    coverage.total === 0 ? 0 : Math.round((coverage.unassigned / coverage.total) * 100);
  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-[#e8edf1] bg-white shadow-sm sm:grid-cols-[repeat(4,1fr)_2.2fr]">
      <CoverageCell label="Schedule lines" value={String(coverage.total)} />
      <CoverageCell label="Assigned" value={String(coverage.assigned)} valueClass="text-emerald-700" />
      <CoverageCell label="Unassigned" value={String(coverage.unassigned)} valueClass="text-rose-700" />
      <CoverageCell label="Coverage" value={`${coverage.coveragePct}%`} />
      <div className="col-span-2 border-t border-slate-100 px-4 py-3 sm:col-span-1 sm:border-l sm:border-t-0">
        <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400">
          Distribution by department
        </div>
        <div
          className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-slate-100"
          role="img"
          aria-label="Department distribution"
        >
          {coverage.segments.map((s) => (
            <span
              key={s.orgId}
              className={twMerge('block h-full', departmentToneForOrgId(s.orgId).dot)}
              style={{ width: `${s.pct}%` }}
              title={`${s.name} · ${s.count}`}
            />
          ))}
          {coverage.unassigned > 0 ? (
            <span
              className="block h-full bg-rose-200"
              style={{ width: `${unassignedPct}%` }}
              title={`Unassigned · ${coverage.unassigned}`}
            />
          ) : null}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500">
          {coverage.segments.map((s) => (
            <span key={s.orgId} className="inline-flex items-center gap-1">
              <span
                className={twMerge('h-1.5 w-1.5 rounded-full', departmentToneForOrgId(s.orgId).dot)}
                aria-hidden
              />
              {s.name} <b className="tabular-nums">{s.count}</b>
            </span>
          ))}
          {coverage.unassigned > 0 ? (
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-300" aria-hidden />
              Unassigned <b className="tabular-nums">{coverage.unassigned}</b>
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 *  Department target card (spec §4.3 right card)
 * ============================================================ */

type DeptTargetCardProps = {
  unit: EpcProjectOrgUnit;
  coverage: DeptCoverage;
  memberships: EpcProjectMembershipRow[];
  selectedCount: number;
  canUpdate: boolean;
  onAssign: (orgId: string) => void;
};

function DeptTargetCard({
  unit,
  coverage,
  memberships,
  selectedCount,
  canUpdate,
  onAssign,
}: DeptTargetCardProps) {
  const id = orgUnitKey(unit);
  const head = departmentHeadForOrg(memberships, id);
  const teamSize = deptTeamSize(memberships, id);
  const load = deptLoadPct(coverage, id);
  const lines = coverage.segments.find((s) => s.orgId === id)?.count ?? 0;
  const blocked = !head;
  const disabled = !canUpdate || selectedCount === 0 || blocked;
  return (
    <div
      className={twMerge(
        'rounded-xl border bg-white px-3 py-3 shadow-sm',
        blocked ? 'border-amber-200/90' : 'border-[#e8edf1]'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span
              className={twMerge('h-2 w-2 shrink-0 rounded-full', departmentToneForOrgId(id).dot)}
              aria-hidden
            />
            <span className="truncate text-sm font-bold text-slate-900">{unit.name}</span>
          </div>
          <p
            className={twMerge(
              'mt-0.5 truncate text-[11px]',
              head ? 'text-slate-500' : 'font-semibold text-amber-700'
            )}
          >
            <Users className="mr-1 inline h-3 w-3" aria-hidden />
            {head
              ? `${head.userName || head.userEmail || 'Head'} · ${teamSize} member${teamSize === 1 ? '' : 's'}`
              : 'No head assigned yet'}
          </p>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onAssign(id)}
          aria-label={`Assign ${selectedCount} to ${unit.name}`}
          className={twMerge(
            'shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] font-bold transition',
            disabled
              ? 'cursor-not-allowed border border-slate-200 bg-white text-slate-400'
              : 'bg-[#006346] text-white shadow-sm hover:brightness-110'
          )}
        >
          Assign {selectedCount} →
        </button>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-[#006346]" style={{ width: `${load}%` }} />
      </div>
      <p className="mt-1 text-[10px] text-slate-500">
        {lines} line{lines === 1 ? '' : 's'} · {load}% of assigned work
      </p>
      {blocked ? (
        <p className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-amber-700">
          <AlertTriangle className="h-3 w-3" aria-hidden />
          Assigning here is blocked until a department head exists
        </p>
      ) : null}
    </div>
  );
}

/* ============================================================
 *  Overwrite confirm (spec §4.3 — re-assignment is an overwrite)
 * ============================================================ */

type OverwriteConfirmProps = {
  count: number;
  deptName: string;
  onCancel: () => void;
  onConfirm: () => void;
};

function OverwriteConfirm({ count, deptName, onCancel, onConfirm }: OverwriteConfirmProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/45 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={onCancel}
    >
      <motion.div
        role="alertdialog"
        aria-modal="true"
        aria-label="Confirm overwrite"
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 8, opacity: 0 }}
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-slate-900/10"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-bold text-slate-900">Overwrite existing assignments?</h3>
        <p className="mt-1.5 text-[12px] leading-relaxed text-slate-600">
          {count} selected line{count === 1 ? ' is' : 's are'} already assigned to a different
          department. Assigning to <b>{deptName}</b> will overwrite {count === 1 ? 'it' : 'them'} and
          clear incompatible owners.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-rose-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm hover:brightness-110"
          >
            Overwrite &amp; assign
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ============================================================
 *  Main view — coverage strip · checkbox WBS tree · dept targets
 * ============================================================ */

export default function EpcScheduleDepartmentAssignView({
  tasks,
  onChange,
  orgUnits,
  memberships,
  canUpdate,
  saving,
  dirty,
  onSave,
}: EpcScheduleDepartmentAssignViewProps) {
  const tree = useMemo(() => buildWbsTree(tasks), [tasks]);
  const activeUnits = useMemo(() => orgUnits.filter((u) => !u.inactive), [orgUnits]);
  const coverage = useMemo(() => computeDeptCoverage(tasks, orgUnits), [tasks, orgUnits]);

  /** When the schedule has a single project-level root (the project name row), skip it and
   *  show its children as the top level — the project root is not assignable to a dept. */
  const displayRoots = useMemo(
    () => (tree.length === 1 && tree[0]!.children.length > 0 ? tree[0]!.children : tree),
    [tree]
  );

  const [expanded, setExpanded] = useState<Set<number>>(() => {
    const rawTree = buildWbsTree(tasks);
    const roots = rawTree.length === 1 && rawTree[0]!.children.length > 0 ? rawTree[0]!.children : rawTree;
    const s = new Set<number>();
    const walk = (n: EpcWbsNode) => {
      s.add(n.rowIndex);
      for (const c of n.children) walk(c);
    };
    for (const n of roots) walk(n);
    return s;
  });
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [filterMode, setFilterMode] = useState<'unassigned' | 'all'>('unassigned');
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState<string>('any');
  const [confirm, setConfirm] = useState<{ orgId: string; overwrites: number } | null>(null);

  const sel = useMemo(() => selectionSummary(tasks, selected), [tasks, selected]);

  /** Display-only hints, precomputed once per task list (spec §4.3 — never auto-applied). */
  const hintByIndex = useMemo(() => {
    const m = new Map<number, string>();
    tasks.forEach((t, i) => {
      if (taskOrgUnitId(t)) return;
      const hint = focusHintForTask(t, orgUnits);
      if (hint) m.set(i, hint);
    });
    return m;
  }, [tasks, orgUnits]);

  const rowMatches = useCallback(
    (i: number): boolean => {
      const t = tasks[i]!;
      const oid = taskOrgUnitId(t);
      if (filterMode === 'unassigned' && oid) return false;
      if (deptFilter !== 'any' && oid !== deptFilter) return false;
      const q = search.trim().toLowerCase();
      if (q && !String(t.name ?? '').toLowerCase().includes(q)) return false;
      return true;
    },
    [tasks, filterMode, deptFilter, search]
  );

  /** A node renders when it matches the filters itself or carries a matching descendant (context). */
  function isNodeVisible(n: EpcWbsNode): boolean {
    return rowMatches(n.rowIndex) || n.children.some(isNodeVisible);
  }
  const visibleRoots = displayRoots.filter(isNodeVisible);

  const toggleExpanded = useCallback((rowIndex: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  }, []);

  const expandAllNodes = useCallback(() => {
    const s = new Set<number>();
    const walk = (n: EpcWbsNode) => {
      s.add(n.rowIndex);
      for (const c of n.children) walk(c);
    };
    for (const n of displayRoots) walk(n);
    setExpanded(s);
  }, [displayRoots]);

  const collapseAllNodes = useCallback(() => setExpanded(new Set()), []);

  const toggleRow = useCallback(
    (rowIndex: number) => {
      setSelected((prev) => toggleSelectionWithDescendants(tasks, prev, rowIndex));
    },
    [tasks]
  );

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const applyAssign = useCallback(
    (orgId: string | undefined) => {
      onChange(applyOrgUnitToTaskIndices(tasks, selected, orgId, memberships));
      setSelected(new Set());
      setConfirm(null);
    },
    [onChange, tasks, selected, memberships]
  );

  const requestAssign = useCallback(
    (orgId: string) => {
      if (!canUpdate || selected.size === 0) return;
      if (!departmentHeadForOrg(memberships, orgId)) return; // belt and braces — the button is disabled too
      const overwrites = countOverwrites(tasks, selected, orgId);
      if (overwrites > 0) {
        setConfirm({ orgId, overwrites });
        return;
      }
      applyAssign(orgId);
    },
    [canUpdate, selected, memberships, tasks, applyAssign]
  );

  const renderNode = (n: EpcWbsNode, depth: number) => {
    if (!isNodeVisible(n)) return null;
    const isExpanded = expanded.has(n.rowIndex);
    const hasKids = n.children.length > 0;
    const oid = taskOrgUnitId(n.task);
    const isSelected = selected.has(n.rowIndex);
    const hint = hintByIndex.get(n.rowIndex);
    const showRow = rowMatches(n.rowIndex);
    return (
      <div key={n.rowIndex}>
        {showRow ? (
          <div
            role={canUpdate ? 'button' : undefined}
            tabIndex={canUpdate ? 0 : undefined}
            aria-pressed={canUpdate ? isSelected : undefined}
            onClick={canUpdate ? () => toggleRow(n.rowIndex) : undefined}
            onKeyDown={
              canUpdate
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleRow(n.rowIndex);
                    }
                  }
                : undefined
            }
            className={twMerge(
              'flex items-center gap-2 border-b border-slate-100/80 px-2 py-1.5 transition-colors',
              canUpdate ? 'cursor-pointer' : '',
              isSelected ? 'bg-[#f6fbf8] shadow-[inset_0_0_0_1px_#aecfbe]' : 'hover:bg-slate-50/80'
            )}
            style={{ paddingLeft: `${depth * 18 + 8}px` }}
          >
            {canUpdate ? (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleRow(n.rowIndex)}
                onClick={(e) => e.stopPropagation()}
                className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-[#006346] focus:ring-[#006346]"
                aria-label={`Select ${n.name}`}
              />
            ) : null}
            {hasKids ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(n.rowIndex);
                }}
                className="grid h-5 w-5 shrink-0 place-items-center rounded text-slate-400 hover:bg-slate-200/60"
                aria-label={isExpanded ? `Collapse ${n.name}` : `Expand ${n.name}`}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="h-5 w-5 shrink-0" />
            )}
            <span
              className={twMerge(
                'min-w-0 flex-1 truncate text-[12.5px] leading-snug',
                n.kind === 'stage'
                  ? 'font-bold text-slate-900'
                  : n.kind === 'task'
                    ? 'font-semibold text-slate-800'
                    : 'text-slate-700'
              )}
              title={n.name}
            >
              {n.name}
            </span>
            {hint ? (
              <span className="hidden shrink-0 text-[10px] italic text-slate-400 md:inline">{hint}</span>
            ) : null}
            {oid ? (
              <span className={twMerge(getDepartmentChipClasses(oid), 'shrink-0 text-[10px]')}>
                <span className="max-w-[7rem] truncate">{orgUnitName(orgUnits, oid) || '—'}</span>
              </span>
            ) : (
              <span className="shrink-0 rounded-md bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">
                Unassigned
              </span>
            )}
          </div>
        ) : null}
        {hasKids && isExpanded ? <div>{n.children.map((c) => renderNode(c, depth + 1))}</div> : null}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3 rounded-2xl border border-[#e8edf1] bg-[#FAFBFA] p-3 sm:p-4"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Departments — manual assignment</h3>
          <p className="text-[11px] text-slate-500">
            Filter → select WBS branches or tasks → assign to a department on the right.
          </p>
        </div>
        {canUpdate ? (
          <button
            type="button"
            disabled={saving || !dirty}
            onClick={() => void onSave()}
            className="inline-flex items-center gap-2 rounded-xl bg-[#006346] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 disabled:opacity-45"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        ) : null}
      </div>

      <CoverageStrip coverage={coverage} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.9fr_1fr]">
        {/* Left — WBS tree with checkboxes */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[#e8edf1] bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-3 py-2">
            <span className="mr-auto text-[11px] font-bold text-slate-700">
              WBS · select branches or tasks
            </span>
            <button
              type="button"
              onClick={expandAllNodes}
              className="rounded-lg border border-slate-200/90 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAllNodes}
              className="rounded-lg border border-slate-200/90 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Collapse all
            </button>
            <button
              type="button"
              onClick={() => setFilterMode('unassigned')}
              className={pillClasses(filterMode === 'unassigned')}
            >
              Unassigned only
            </button>
            <button
              type="button"
              onClick={() => setFilterMode('all')}
              className={pillClasses(filterMode === 'all')}
            >
              All
            </button>
            <label className="relative">
              <Search
                className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search tasks"
                placeholder="Search"
                className="h-6 w-32 rounded-full border border-slate-200 bg-white pl-6 pr-2 text-[11px] outline-none focus:border-[#006346]"
              />
            </label>
            <select
              aria-label="Filter by department"
              value={deptFilter}
              onChange={(e) => {
                const next = e.target.value;
                setDeptFilter(next);
                if (next !== 'any' && filterMode === 'unassigned') setFilterMode('all');
              }}
              className="h-6 rounded-full border border-slate-200 bg-white px-2 text-[11px] text-slate-600 outline-none focus:border-[#006346]"
            >
              <option value="any">All departments</option>
              {activeUnits.map((u) => (
                <option key={orgUnitKey(u)} value={orgUnitKey(u)}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div className="max-h-[min(560px,62vh)] flex-1 overflow-auto">
            {tree.length === 0 ? (
              <div className="px-6 py-14 text-center text-sm text-slate-500">
                No tasks yet — import MPP or add rows.
              </div>
            ) : visibleRoots.length === 0 ? (
              <div className="px-6 py-14 text-center text-sm text-slate-500">
                No rows match the current filters.
              </div>
            ) : (
              visibleRoots.map((s) => renderNode(s, 0))
            )}
          </div>
          {selected.size > 0 ? (
            <div className="sticky bottom-0 z-10 m-2 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-[12px] text-slate-300 shadow-lg">
              <span className="flex items-center gap-1.5">
                <b className="font-bold text-white">
                  {sel.lines} line{sel.lines === 1 ? '' : 's'} selected
                </b>
                <span>
                  across {sel.branches} {sel.branches === 1 ? 'branch' : 'branches'}
                </span>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="ml-2 text-slate-400 underline-offset-2 hover:text-white hover:underline"
                >
                  clear ✕
                </button>
              </span>
              <span className="hidden text-slate-400 sm:inline">→ pick a department on the right</span>
            </div>
          ) : null}
        </div>

        {/* Right — department targets */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[#e8edf1] bg-white shadow-sm">
          <div className="border-b border-slate-100 px-3 py-2">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700">
              <Building2 className="h-3.5 w-3.5 text-[#006346]" aria-hidden />
              Assign to department
            </div>
            <p className="mt-0.5 text-[10.5px] text-slate-500">
              {selected.size === 0
                ? 'Select rows on the left first.'
                : `${selected.size} line${selected.size === 1 ? '' : 's'} ready to assign.`}
            </p>
          </div>
          <div className="max-h-[min(560px,62vh)] flex-1 space-y-2 overflow-auto p-3">
            {canUpdate && selected.size > 0 ? (
              <button
                type="button"
                onClick={() => applyAssign(undefined)}
                className="flex w-full items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-left text-[12px] font-medium text-slate-600 transition hover:border-rose-300 hover:bg-rose-50/50"
              >
                <X className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                Clear department from {selected.size} selected line{selected.size === 1 ? '' : 's'}
              </button>
            ) : null}
            {activeUnits.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-5 text-sm text-slate-600">
                <p className="font-semibold text-slate-800">No departments yet</p>
                <p className="mt-1 text-xs text-slate-500">
                  Add departments (and a department head for each) under{' '}
                  <strong>Team &amp; hierarchy</strong> to start routing schedule lines.
                </p>
              </div>
            ) : (
              activeUnits.map((u) => (
                <DeptTargetCard
                  key={orgUnitKey(u)}
                  unit={u}
                  coverage={coverage}
                  memberships={memberships}
                  selectedCount={selected.size}
                  canUpdate={canUpdate}
                  onAssign={requestAssign}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {!canUpdate ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-2 text-[11px] text-amber-900">
          View only — assigning departments requires{' '}
          <code className="rounded bg-amber-100/80 px-1 font-mono">epcProject.update</code>.
        </div>
      ) : null}

      <AnimatePresence>
        {confirm ? (
          <OverwriteConfirm
            count={confirm.overwrites}
            deptName={orgUnitName(orgUnits, confirm.orgId) || '—'}
            onCancel={() => setConfirm(null)}
            onConfirm={() => applyAssign(confirm.orgId)}
          />
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}
